/**
 * GracePeriodBanner Component
 *
 * App-wide banner displayed when subscription is in grace period (past_due).
 * Shows remaining days and prompts user to update payment method.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import Animated, { FadeInDown, FadeOutUp } from 'react-native-reanimated';
import { AlertTriangle, CreditCard, X } from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { subscriptionLifecycle } from '@/lib/stripe/subscription-lifecycle';

// ============================================================================
// Types
// ============================================================================

interface GracePeriodBannerProps {
  onUpdatePayment: () => void;
  onDismiss?: () => void;
  dismissible?: boolean;
}

// ============================================================================
// Main Component
// ============================================================================

export function GracePeriodBanner({
  onUpdatePayment,
  onDismiss,
  dismissible = false,
}: GracePeriodBannerProps) {
  const colors = useThemeColors();

  const subscription = useSubscriptionStore((s) => s.subscription);
  const gracePeriodEnd = useSubscriptionStore((s) => s.gracePeriodEnd);
  const paymentRetryCount = useSubscriptionStore((s) => s.paymentRetryCount);

  // Don't show if not past_due
  if (!subscription || subscription.status !== 'past_due') {
    return null;
  }

  const daysRemaining = subscriptionLifecycle.getGracePeriodDaysRemaining();
  const isUrgent = daysRemaining <= 2;

  return (
    <Animated.View
      entering={FadeInDown.duration(300)}
      exiting={FadeOutUp.duration(200)}
      style={[
        styles.container,
        {
          backgroundColor: isUrgent ? colors.traffic.red : colors.traffic.amber,
        },
      ]}
    >
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <AlertTriangle size={20} color="#fff" />
        </View>

        <View style={styles.textContainer}>
          <Text style={styles.title}>
            {isUrgent ? 'Payment Required' : 'Payment Failed'}
          </Text>
          <Text style={styles.message}>
            {daysRemaining > 0
              ? `Update your payment method within ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} to keep your subscription`
              : 'Your subscription will be canceled today unless you update your payment method'}
          </Text>
        </View>

        <AnimatedPressable
          onPress={onUpdatePayment}
          style={styles.button}
        >
          <CreditCard size={16} color={isUrgent ? colors.traffic.red : colors.traffic.amber} />
          <Text
            style={[
              styles.buttonText,
              { color: isUrgent ? colors.traffic.red : colors.traffic.amber },
            ]}
          >
            Update
          </Text>
        </AnimatedPressable>

        {dismissible && onDismiss && (
          <AnimatedPressable
            onPress={onDismiss}
            style={styles.dismissButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <X size={18} color="rgba(255,255,255,0.7)" />
          </AnimatedPressable>
        )}
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Compact Variant
// ============================================================================

interface GracePeriodBannerCompactProps {
  onPress: () => void;
}

export function GracePeriodBannerCompact({ onPress }: GracePeriodBannerCompactProps) {
  const colors = useThemeColors();

  const subscription = useSubscriptionStore((s) => s.subscription);

  if (!subscription || subscription.status !== 'past_due') {
    return null;
  }

  const daysRemaining = subscriptionLifecycle.getGracePeriodDaysRemaining();
  const isUrgent = daysRemaining <= 2;

  return (
    <AnimatedPressable
      onPress={onPress}
      style={[
        styles.compactContainer,
        {
          backgroundColor: isUrgent
            ? `${colors.traffic.red}15`
            : `${colors.traffic.amber}15`,
          borderColor: isUrgent ? colors.traffic.red : colors.traffic.amber,
        },
      ]}
    >
      <AlertTriangle
        size={16}
        color={isUrgent ? colors.traffic.red : colors.traffic.amber}
      />
      <Text
        style={[
          styles.compactText,
          { color: isUrgent ? colors.traffic.red : colors.traffic.amber },
        ]}
      >
        {daysRemaining > 0
          ? `Payment issue - ${daysRemaining}d left`
          : 'Payment required today'}
      </Text>
    </AnimatedPressable>
  );
}

// ============================================================================
// Hook for Banner Visibility
// ============================================================================

export function useShowGracePeriodBanner(): boolean {
  const subscription = useSubscriptionStore((s) => s.subscription);
  return subscription?.status === 'past_due';
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 2,
  },
  message: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    lineHeight: 16,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  dismissButton: {
    padding: 4,
    marginLeft: 4,
  },
  // Compact styles
  compactContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  compactText: {
    fontSize: 13,
    fontWeight: '500',
  },
});
